/**
 * Tailwind CSS implementation of Textarea component
 * Uses Seeds textarea CSS classes from textarea.css
 */

import React from "react";
import type { TypeTextareaProps } from "./TextareaTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const TextareaTailwind = React.forwardRef<
  HTMLTextAreaElement,
  TypeTextareaProps
>(
  (
    {
      autoFocus,
      disabled = false,
      readOnly,
      isInvalid = false,
      id,
      name,
      placeholder,
      value,
      enableSpellcheck,
      enableResize = false,
      required,
      rows = 4,
      elemBefore,
      elemAfter,
      maxLength,
      ariaLabel,
      ariaDescribedby,
      innerRef: _innerRef,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onKeyUp,
      // `color` collides with the native attribute and only ever reaches the
      // styled-components path; strip it so it never lands on the container.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      color: _color,
      className,
      qa = {},
      inputProps = {},
      ...rest
    }: TypeTextareaProps,
    ref: React.Ref<HTMLTextAreaElement>
  ) => {
    const containerClasses = cn(
      "seeds-textarea",
      {
        "seeds-textarea-disabled": disabled,
      },
      className
    );

    const inputClasses = cn("seeds-textarea-input", {
      "seeds-textarea-input-resizable": enableResize,
      "seeds-textarea-input-before": !!elemBefore,
      "seeds-textarea-input-after": !!elemAfter,
      "seeds-textarea-input-disabled": disabled,
      "seeds-textarea-input-invalid": isInvalid,
    });

    // `rest` may still type as carrying styled-system props (mt/px/bg/…), but
    // needsStyledComponents() already routed any such instance to the SC path,
    // so they never reach here at runtime. Widen the element type so the spread
    // type-checks — same approach as ButtonTailwind's `Component … as any`.
    const Container = "div" as any;

    return (
      <Container
        className={containerClasses}
        data-qa-textarea={id}
        data-qa-textarea-isdisabled={disabled === true}
        data-qa-textarea-isrequired={required === true}
        data-qa-textarea-isinvalid={isInvalid === true}
        {...qa}
        {...rest}
      >
        {elemBefore && (
          <div className="seeds-textarea-accessory seeds-textarea-accessory-before">
            {elemBefore}
          </div>
        )}

        <textarea
          className={inputClasses}
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-invalid={isInvalid}
          value={value}
          name={name}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          readOnly={Boolean(readOnly)}
          autoFocus={autoFocus}
          spellCheck={enableSpellcheck}
          required={required}
          maxLength={maxLength}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          ref={ref}
          data-qa-textarea-input=""
          data-qa-input={name}
          {...inputProps}
        />

        {elemAfter && (
          <div className="seeds-textarea-accessory seeds-textarea-accessory-after">
            {elemAfter}
          </div>
        )}
      </Container>
    );
  }
);

TextareaTailwind.displayName = "TextareaTailwind";
